import { ChangeDetectionStrategy, Component, ViewEncapsulation, input, output } from '@angular/core';
import type { ContextGuardNoticeBand } from './context.guard.types';

/**
 * Conversation-length notice for the agent composer stack.
 *
 * Purely presentational: the host decides WHICH band to show (and whether to show anything at all),
 * this component only renders it and reports the two things a user can do about it.
 *
 * One notice, three bands. Every band shares the same box, the same title/body hierarchy and the
 * same call to action rendered inline at the end of the body, so the family reads as one composer
 * system at both fullscreen and popup widths:
 *  - `advisory` and `approaching` sit above the composer in neutral and amber treatments. Both are
 *    dismissible and the composer keeps working — including Stop while a reply is still streaming.
 *  - `blocked` takes the composer's place, borrowing its footprint and box so the area neither
 *    collapses nor shifts. It is solid neutral grey, states that the chat is read-only, and has no
 *    dismiss. The host owns the actual input lock and keeps the read-only helper row beneath it.
 *
 * The transcript above is untouched in every band: it stays readable, copyable and downloadable.
 */
@Component({
  selector: 'agent-context-guard-notice',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './context.guard.notice.component.html',
  styleUrls: ['./context.guard.notice.component.less'],
})
export class AgentContextGuardNoticeComponent {

  public readonly $band = input.required<ContextGuardNoticeBand>({ alias: 'band' });

  /** The user asked to start a fresh chat. The host owns the actual lifecycle. */
  public readonly newChat = output<void>();
  /** The user dismissed an advisory or approaching notice. Never emitted for `blocked`. */
  public readonly dismiss = output<void>();
}
